import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  completeProductPhotoCapture,
  getProductPhotoCaptureStatus,
  postProductPhotoCaptureImages,
} from '../utils/franchisePriceProductsApi';
import './ProductPhotoCapturePage.css';

const MAX_IMAGES = 6;
const FACING_ENV = 'environment';

function domainLabel(domain) {
  return String(domain || 'skin').toLowerCase() === 'hair' ? 'Hair' : 'Skin';
}

export default function ProductPhotoCapturePage() {
  const { token } = useParams();
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const fileInputRef = useRef(null);

  const [sessionOk, setSessionOk] = useState(false);
  const [sessionError, setSessionError] = useState('');
  const [productDomain, setProductDomain] = useState('skin');
  const [brandName, setBrandName] = useState('');
  const [brandLogo, setBrandLogo] = useState('');
  const [logoFailed, setLogoFailed] = useState(false);
  const [cameraError, setCameraError] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [shots, setShots] = useState([]);

  const stopStream = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  }, []);

  const startCamera = useCallback(async () => {
    stopStream();
    setCameraError('');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: FACING_ENV },
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
    } catch (_) {
      setCameraError('Camera blocked — use Gallery instead.');
    }
  }, [stopStream]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!token) {
        setSessionError('Invalid link');
        return;
      }
      try {
        const info = await getProductPhotoCaptureStatus(token);
        if (cancelled) return;
        const brand = info.branding || {};
        if (brand.logo) setBrandLogo(String(brand.logo));
        if (brand.name) setBrandName(String(brand.name).trim());
        if (info.productDomain) setProductDomain(info.productDomain);
        if (info.status === 'expired' || info.status === 'consumed') {
          setSessionError(info.status === 'consumed' ? 'Already used.' : 'Link expired.');
          return;
        }
        if (info.status === 'ready') {
          setSent(true);
          setSessionOk(true);
          return;
        }
        setSessionOk(true);
      } catch (err) {
        if (!cancelled) setSessionError(err.message || 'Link unavailable');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  useEffect(() => {
    if (!sessionOk || sent) return undefined;
    void startCamera();
    return () => stopStream();
  }, [sessionOk, sent, startCamera, stopStream]);

  const captureFrame = () => {
    if (shots.length >= MAX_IMAGES) {
      setCameraError(`Max ${MAX_IMAGES} photos`);
      return;
    }
    const video = videoRef.current;
    if (!video || !video.videoWidth) {
      setCameraError('Camera not ready');
      return;
    }
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0);
    const dataUrl = canvas.toDataURL('image/jpeg', 0.92);
    setShots((prev) => [...prev, dataUrl].slice(0, MAX_IMAGES));
    setCameraError('');
  };

  const onFiles = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;
    const room = MAX_IMAGES - shots.length;
    if (room <= 0) {
      setCameraError(`Max ${MAX_IMAGES} photos`);
      return;
    }
    const selected = files.slice(0, room);
    Promise.all(
      selected.map(
        (file) =>
          new Promise((resolve) => {
            const reader = new FileReader();
            reader.onload = () => resolve(String(reader.result || ''));
            reader.onerror = () => resolve('');
            reader.readAsDataURL(file);
          }),
      ),
    ).then((urls) => {
      const next = urls.filter(Boolean);
      if (!next.length) return;
      setShots((prev) => [...prev, ...next].slice(0, MAX_IMAGES));
      setCameraError('');
    });
  };

  const removeShot = (index) => {
    setShots((prev) => prev.filter((_, i) => i !== index));
  };

  const sendAll = async () => {
    if (!shots.length || !token) return;
    setBusy(true);
    setCameraError('');
    try {
      await postProductPhotoCaptureImages(token, shots);
      await completeProductPhotoCapture(token);
      setSent(true);
      stopStream();
    } catch (err) {
      setCameraError(err.message || 'Send failed');
    } finally {
      setBusy(false);
    }
  };

  const showLogo = Boolean(brandLogo) && !logoFailed;
  const title = `${domainLabel(productDomain)} product photos`;

  const brandBar = (
    <header className="product-photo-capture__brand">
      {showLogo ? (
        <img
          src={brandLogo}
          alt={brandName || 'Outlet'}
          className="product-photo-capture__logo"
          onError={() => setLogoFailed(true)}
        />
      ) : (
        <span className="product-photo-capture__brand-fallback">{brandName || 'Nyafaz'}</span>
      )}
      <h1 className="product-photo-capture__title">{title}</h1>
    </header>
  );

  if (sessionError) {
    return (
      <div className="product-photo-capture product-photo-capture--status">
        {brandBar}
        <div className="product-photo-capture__card">
          <h2>Unavailable</h2>
          <p>{sessionError}</p>
        </div>
      </div>
    );
  }

  if (!sessionOk) {
    return (
      <div className="product-photo-capture product-photo-capture--status">
        {brandBar}
        <div className="product-photo-capture__card">
          <p>Opening…</p>
        </div>
      </div>
    );
  }

  if (sent) {
    return (
      <div className="product-photo-capture product-photo-capture--status">
        {brandBar}
        <div className="product-photo-capture__card product-photo-capture__card--ok">
          <h2>Sent</h2>
          <p>Photos are on the partner desk. You can close this page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="product-photo-capture">
      {brandBar}

      <div className="product-photo-capture__stage">
        <video ref={videoRef} className="product-photo-capture__video" playsInline muted autoPlay />
      </div>

      {shots.length ? (
        <ul className="product-photo-capture__tray" aria-label="Captured photos">
          {shots.map((src, index) => (
            <li key={`shot-${index}`}>
              <img src={src} alt={`Photo ${index + 1}`} />
              <button type="button" onClick={() => removeShot(index)} aria-label={`Remove photo ${index + 1}`}>
                ×
              </button>
            </li>
          ))}
        </ul>
      ) : null}

      {cameraError ? <p className="product-photo-capture__error">{cameraError}</p> : null}

      <div className="product-photo-capture__actions">
        <button
          type="button"
          className="product-photo-capture__btn"
          onClick={() => fileInputRef.current?.click()}
          disabled={busy || shots.length >= MAX_IMAGES}
        >
          Gallery
        </button>
        <button
          type="button"
          className="product-photo-capture__btn product-photo-capture__btn--primary"
          onClick={captureFrame}
          disabled={busy || shots.length >= MAX_IMAGES}
        >
          Capture
        </button>
        <button
          type="button"
          className="product-photo-capture__btn product-photo-capture__btn--primary"
          onClick={() => void sendAll()}
          disabled={busy || !shots.length}
        >
          {busy ? 'Sending…' : `Send (${shots.length})`}
        </button>
      </div>

      <p className="product-photo-capture__hint">
        Rear camera · up to {MAX_IMAGES} photos of one product (front, back, ingredients).
      </p>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        className="product-photo-capture__file"
        onChange={onFiles}
      />
    </div>
  );
}
